/**
 * Cómo se nombran los pisos de un modelo IFC en la pantalla: "Nivel 1 · +0.00 m", numerados por su
 * elevación REAL de abajo hacia arriba, con el nombre que trae el modelo ("Erdgeschoss") en chico
 * debajo. Sin tablas de traducción: el número y la altura salen del propio modelo.
 */
export interface NivelParaEtiquetar {
  id: number;
  nombre: string;
  elevacion: number | null;
  /** Una PARTE de infraestructura (IfcFacilityPart) se nombra como la nombra el modelo, sin número. */
  tipo?: 'piso' | 'parte';
  /** El modelo no trae pisos: este se dedujo de sus losas de piso. Se dice en el título. */
  deducido?: boolean;
}

export interface EtiquetaNivel {
  /** "Nivel 1 · +0.00 m" (sin altura si el modelo no la dice). */
  titulo: string;
  /** "Nivel 1" (para nombrar una pieza: "Muro 3 · Nivel 1 · 10.43 m²"). */
  corto: string;
  /** Nombre del piso tal como viene en el modelo. */
  nombreDelModelo: string;
}

function fmtAltura(m: number): string {
  const abs = Math.abs(m).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${m < 0 ? '−' : '+'}${abs} m`;
}

export function etiquetasDeNiveles(niveles: readonly NivelParaEtiquetar[]): Map<number, EtiquetaNivel> {
  // Con elevación primero (de abajo hacia arriba); los que no la dicen, al final en su orden.
  const orden = niveles
    .map((n, i) => ({ n, i }))
    .sort((a, b) => {
      const ea = a.n.elevacion;
      const eb = b.n.elevacion;
      if (ea == null && eb == null) return a.i - b.i;
      if (ea == null) return 1;
      if (eb == null) return -1;
      return ea - eb || a.i - b.i;
    });
  const out = new Map<number, EtiquetaNivel>();
  let k = 0;
  orden.forEach(({ n }) => {
    if (n.tipo === 'parte') {
      out.set(n.id, { titulo: n.nombre, corto: n.nombre, nombreDelModelo: n.nombre });
      return;
    }
    const altura = n.elevacion != null && Number.isFinite(n.elevacion) ? ` · ${fmtAltura(n.elevacion)}` : '';
    k++;
    out.set(n.id, { titulo: `Nivel ${k}${altura}${n.deducido ? ' (deducido)' : ''}`, corto: `Nivel ${k}`, nombreDelModelo: n.nombre });
  });
  return out;
}
